import React from 'react';
import { ArrowRight, Download, Mail, Sparkles, ShieldCheck, Code, Globe } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';
import { LinkedinIcon, GithubIcon, WhatsappIcon } from './SocialIcons';

export const Hero: React.FC = () => {
  return (
    <section id="home" className="hero-section">
      {/* Background Decorative Glow Blobs */}
      <div className="glow-blob blob-1"></div>
      <div className="glow-blob blob-2"></div>

      <div className="container hero-container">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} className="badge-sparkle" />
            <span>{portfolioData.role}</span>
          </div>

          <h1 className="hero-title">
            Crafting High-Scale <br />
            <span className="text-gradient">Digital Ecosystems</span>
          </h1>

          <p className="hero-subtitle">
            {portfolioData.intro}
          </p>

          <div className="hero-actions">
            <a href="#contact" className="btn btn-primary">
              <span>Let's Build Together</span>
              <ArrowRight size={18} />
            </a>
            <a
              href={portfolioData.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <Download size={18} />
              <span>Download CV</span>
            </a>
          </div>

          <div className="hero-socials">
            <span className="social-label">Connect with me:</span>
            <div className="social-icons">
              <a
                href={portfolioData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                title="LinkedIn Profile"
              >
                <LinkedinIcon size={20} />
              </a>
              {portfolioData.github && (
                <a
                  href={portfolioData.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  title="GitHub Profile"
                >
                  <GithubIcon size={20} />
                </a>
              )}
              {portfolioData.whatsappUrl && (
                <a
                  href={portfolioData.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  title={`WhatsApp: @${portfolioData.whatsappUsername}`}
                >
                  <WhatsappIcon size={20} />
                </a>
              )}
              <a href={`mailto:${portfolioData.email}`} aria-label="Email" title="Direct Email">
                <Mail size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="profile-wrapper">
            <div className="profile-glow"></div>
            <div className="profile-card glass-card">
              <img
                src={portfolioData.profilePic}
                alt={portfolioData.fullName}
                className="profile-img"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
              <div className="profile-info-overlay">
                <h3>{portfolioData.fullName}</h3>
                <p>{portfolioData.address}</p>
              </div>
            </div>

            {/* Floating Info Pill 1 */}
            <div className="floating-pill pill-exp glass-card">
              <div className="pill-icon exp-icon">
                <ShieldCheck size={20} />
              </div>
              <div>
                <span className="pill-num">{portfolioData.stats.yearsExp}+ Yrs</span>
                <span className="pill-text">Full Stack Exp</span>
              </div>
            </div>

            {/* Floating Info Pill 2 */}
            <div className="floating-pill pill-projects glass-card">
              <div className="pill-icon prj-icon">
                <Code size={20} />
              </div>
              <div>
                <span className="pill-num">{portfolioData.stats.projectsCompleted}+</span>
                <span className="pill-text">Projects Delivered</span>
              </div>
            </div>

            {/* Floating Info Pill 3 */}
            <div className="floating-pill pill-clients glass-card">
              <div className="pill-icon client-icon">
                <Globe size={20} />
              </div>
              <div>
                <span className="pill-num">{portfolioData.clients[0]?.name.split(' ')[0]} & {portfolioData.clients[1]?.name.split(' ')[0]}</span>
                <span className="pill-text">Key Clients</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hero-section {
          min-height: 100vh;
          padding-top: 8rem;
          padding-bottom: 4rem;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .glow-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.25;
          z-index: 0;
          pointer-events: none;
        }

        .blob-1 {
          width: 500px;
          height: 500px;
          background: #6366f1;
          top: -100px;
          right: -100px;
        }

        .blob-2 {
          width: 400px;
          height: 400px;
          background: #06b6d4;
          bottom: -50px;
          left: -50px;
        }

        .hero-container {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 4rem;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 1.1rem;
          border-radius: var(--radius-full);
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid var(--border-highlight);
          color: var(--accent-primary);
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .badge-sparkle {
          color: var(--accent-secondary);
          animation: pulseGlow 2s infinite ease-in-out;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1.15;
          letter-spacing: -0.03em;
          margin-bottom: 1.5rem;
        }

        .hero-subtitle {
          font-size: 1.15rem;
          color: var(--text-muted);
          line-height: 1.7;
          margin-bottom: 2.5rem;
          max-width: 620px;
        }

        .hero-actions {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .hero-socials {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .social-label {
          font-size: 0.9rem;
          color: var(--text-dim);
          font-weight: 500;
        }

        .social-icons {
          display: flex;
          gap: 0.75rem;
        }

        .social-icons a {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .social-icons a:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
          transform: translateY(-3px);
          box-shadow: var(--shadow-glow);
        }

        .hero-visual {
          position: relative;
          display: flex;
          justify-content: center;
        }

        .profile-wrapper {
          position: relative;
          width: 320px;
          height: 380px;
        }

        .profile-glow {
          position: absolute;
          inset: -15px;
          background: var(--gradient-brand);
          border-radius: var(--radius-lg);
          filter: blur(25px);
          opacity: 0.4;
          z-index: 0;
        }

        .profile-card {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: var(--radius-lg);
          background: var(--bg-secondary);
          display: flex;
          flex-direction: column;
        }

        .profile-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }

        .profile-info-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1.25rem;
          background: linear-gradient(to top, rgba(9, 13, 22, 0.95), transparent);
          color: #fff;
        }

        .profile-info-overlay h3 {
          font-size: 1.2rem;
          font-weight: 700;
        }

        .profile-info-overlay p {
          font-size: 0.85rem;
          color: var(--text-muted);
        }

        .floating-pill {
          position: absolute;
          z-index: 2;
          padding: 0.75rem 1.1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-md);
          animation: float 5s ease-in-out infinite;
        }

        .pill-exp {
          top: -20px;
          left: -40px;
        }

        .pill-projects {
          bottom: 40px;
          right: -50px;
          animation-delay: 2.5s;
        }

        .pill-clients {
          bottom: -25px;
          left: -20px;
          animation-delay: 1.2s;
        }

        .pill-icon {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
        }

        .exp-icon { background: linear-gradient(135deg, #6366f1, #4f46e5); }
        .prj-icon { background: linear-gradient(135deg, #06b6d4, #0891b2); }
        .client-icon { background: linear-gradient(135deg, #ec4899, #db2777); }

        .pill-num {
          display: block;
          font-weight: 800;
          font-size: 0.95rem;
          color: var(--text-main);
          line-height: 1.2;
        }

        .pill-text {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        @media (max-width: 992px) {
          .hero-container {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 3rem;
          }
          .hero-subtitle {
            margin-left: auto;
            margin-right: auto;
          }
          .hero-actions {
            justify-content: center;
          }
          .hero-socials {
            justify-content: center;
          }
          .pill-exp { left: -10px; }
          .pill-projects { right: -10px; }
        }

        @media (max-width: 576px) {
          .hero-title {
            font-size: 2.4rem;
          }
          .profile-wrapper {
            width: 280px;
            height: 340px;
          }
          .floating-pill {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};
